/**
 * Urlopy i absencje (spec 03 §4) — salda i kreator wniosku.
 *
 * Makieta kwitowała „Złóż wniosek" toastem „Otwieram kreator…"; tutaj kreator
 * istnieje, liczy dni robocze (bez weekendów i świąt PL) i pilnuje puli, także
 * puli nadrzędnej dla urlopu na żądanie.
 *
 * Od Fali 7 (grupy 24, 35 i 44, D102) karta pokazuje pulę z tej samej reguły
 * co kadry i asystent: dni I godziny, bo przy niepełnym etacie jedno bez
 * drugiego wprowadza w błąd. Przy umowie cywilnej urlop nie przysługuje
 * (art. 152 § 1 k.p.) — zamiast kart z zerami jest komunikat, bez kreatora.
 * Po ustaniu zatrudnienia historia zostaje do wglądu, ale wniosku się nie
 * składa (art. 171 § 1 k.p.).
 */
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import {
  Button,
  Calendar,
  Card,
  EmptyState,
  InfoBanner,
  Spinner,
  StatCard,
  StatusPill,
} from '@k1p4/ui'
import { formatDate } from '@k1p4/i18n'
import { api, apiToday, type LeaveBalanceItem, type RequestStatus } from '../../api/client'
import { LeaveRequestModal } from './LeaveRequestModal'
import { formatCount, pluralUnit } from './units'
import styles from './portal.module.css'

const STATUS_TONE: Record<string, 'warning' | 'success' | 'danger' | 'neutral'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
  change_requested: 'warning',
}

export function PortalLeaves() {
  const { t, i18n } = useTranslation()
  /* Kreator wniosku jest wspólny z kafelkami urlopowymi „Wniosków" (grupa 40). */
  const [formOpen, setFormOpen] = useState(false)

  const leaves = useQuery({ queryKey: ['me', 'leaves'], queryFn: () => api.meLeaves() })

  const balances = leaves.data?.balances
  /* Kreator proponuje wyłącznie typy z pulą — reszta wymaga rozmowy z HR. */
  const selectableTypes = useMemo(
    () => (balances ?? []).filter((balance) => balance.limitDays !== null),
    [balances],
  )

  /** Nota karty: limit w dniach, a przy puli w godzinach — także godziny (D102). */
  function balanceNote(balance: LeaveBalanceItem, year: number): string {
    const limit = balance.limitDays ?? 0
    return balance.limitHours !== null && balance.limitHours !== undefined
      ? t('portal.leaves.balanceNoteHours', { count: limit, hours: balance.limitHours, year })
      : t('portal.leaves.balanceNote', { count: limit, year })
  }

  if (leaves.isPending) return <Spinner label={t('common.loading')} />
  if (leaves.isError) {
    return <EmptyState title={t('common.error')} description={t('common.retry')} />
  }
  if (!leaves.data.hasEmployeeRecord) {
    return <EmptyState icon={<Calendar size={28} />} title={t('portal.noRecord')} />
  }

  const allBalances = leaves.data.balances
  const year = leaves.data.year ?? apiToday().getFullYear()
  /* Umowa cywilna: urlop nie przysługuje (art. 152 § 1 k.p.) — bez kart i kreatora. */
  const notApplicable = leaves.data.leaveEntitlement === 'nie_dotyczy'
  const employment = leaves.data.employment
  /* Po ustaniu wniosku się nie składa, a historia zostaje (art. 171 § 1 k.p., grupa 44). */
  const ended = employment?.ended === true
  const canRequest = !notApplicable && !ended
  const holiday = allBalances.find((balance) => balance.typeKey === 'wypoczynkowy')
  const workDayHours = leaves.data.workDayHours ?? null
  const holidayHours = holiday?.remainingHours ?? null

  return (
    <>
      {notApplicable ? (
        <InfoBanner tone="info" className={styles.banner}>
          {t('portal.leaves.notApplicable')}
        </InfoBanner>
      ) : null}
      {ended ? (
        <InfoBanner tone="info" className={styles.banner}>
          {employment?.endDate
            ? t('portal.leaves.employmentEnded', { date: formatDate(employment.endDate, 'dot') })
            : t('portal.leaves.employmentEndedNoDate')}
        </InfoBanner>
      ) : employment?.endDate ? (
        <InfoBanner tone="info" className={styles.banner}>
          {t('portal.leaves.employmentEnding', { date: formatDate(employment.endDate, 'dot') })}
        </InfoBanner>
      ) : null}

      {notApplicable ? null : (
        <>
          <div className={styles.kpiGrid}>
            {selectableTypes.map((balance) => (
              <StatCard
                key={balance.typeKey}
                label={i18n.language.startsWith('pl') ? balance.labelPl : balance.labelEn}
                value={formatCount(i18n.language, balance.remainingDays ?? 0)}
                unit={pluralUnit(i18n.language, balance.remainingDays ?? 0, t, 'day')}
                note={
                  balance.subpoolOf
                    ? t('portal.leaves.subpool', {
                        parent:
                          allBalances.find((item) => item.typeKey === balance.subpoolOf)?.[
                            i18n.language.startsWith('pl') ? 'labelPl' : 'labelEn'
                          ] ??
                          balance.subpoolOf,
                      })
                    : balanceNote(balance, year)
                }
              />
            ))}
          </div>

          {/*
            Skąd liczba na karcie (Fala 7, grupa 24): dzień urlopu to 8 h, więc
            przy niepełnym etacie pula w godzinach starcza na więcej dni pracy,
            niż pokazują dni urlopu; w roku ustania wymiar jest proporcjonalny;
            a wymiar podstawowy (20 albo 26 dni) zależy od stażu, którego system
            nie zna.
          */}
          {holiday ? (
            <div className={styles.poolNotes}>
              {workDayHours !== null && workDayHours < 8 && holidayHours !== null ? (
                <p className={styles.rowNote}>
                  {t('portal.leaves.workDayNote', {
                    count: Math.round((holidayHours / workDayHours) * 100) / 100,
                    dayHours: workDayHours,
                    hours: holidayHours,
                  })}
                </p>
              ) : null}
              {holiday.proportionalMonths !== null && holiday.proportionalMonths !== undefined ? (
                <p className={styles.rowNote}>
                  {t('portal.leaves.proportionalNote', { count: holiday.proportionalMonths })}
                </p>
              ) : null}
              {holiday.baseAssumed ? (
                <p className={styles.rowNote}>{t('portal.leaves.baseAssumedNote')}</p>
              ) : null}
            </div>
          ) : null}
        </>
      )}

      {canRequest ? (
        <Card pad="md" className={styles.cta}>
          <div>
            <p className={styles.ctaTitle}>{t('portal.leaves.ctaTitle')}</p>
            <p className={styles.ctaBody}>{t('portal.leaves.ctaBody')}</p>
          </div>
          <Button onClick={() => setFormOpen(true)}>
            {t('portal.leaves.ctaButton')}
          </Button>
        </Card>
      ) : null}

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('portal.leaves.absencesTitle')}</h2>
        {leaves.data.absences.length === 0 ? (
          <EmptyState title={t('portal.leaves.empty')} description={t('portal.leaves.emptyBody')} />
        ) : (
          <Card pad="flush" className={styles.list}>
            {leaves.data.absences.map((absence) => {
              const label =
                allBalances.find((item) => item.typeKey === absence.typeKey)?.[
                  i18n.language.startsWith('pl') ? 'labelPl' : 'labelEn'
                ] ?? absence.typeKey
              return (
                <div key={absence.id} className={styles.row}>
                  <span className={styles.rowIcon} aria-hidden="true">
                    <Calendar size={18} />
                  </span>
                  <div className={styles.rowBody}>
                    <p className={styles.rowTitle}>{label}</p>
                    <p className={styles.rowMeta}>
                      {formatDate(absence.dateFrom, 'dot')} – {formatDate(absence.dateTo, 'dot')} ·{' '}
                      {absence.days} {pluralUnit(i18n.language, absence.days, t, 'day')}
                    </p>
                    {absence.decisionComment ? (
                      <p className={styles.rowNote}>
                        {t('portal.leaves.decision', { text: absence.decisionComment })}
                      </p>
                    ) : null}
                  </div>
                  <StatusPill tone={STATUS_TONE[absence.status] ?? 'neutral'}>
                    {t(`portalRequestStatus.${absence.status as RequestStatus}`, {
                      defaultValue: absence.status,
                    })}
                  </StatusPill>
                </div>
              )
            })}
          </Card>
        )}
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('portal.leaves.sickTitle')}</h2>
        {leaves.data.sickLeaves.length === 0 ? (
          <p className={styles.rowNote}>{t('portal.leaves.sickEmpty')}</p>
        ) : (
          <Card pad="flush" className={styles.list}>
            {leaves.data.sickLeaves.map((item) => (
              <div key={item.id} className={styles.row}>
                <span className={styles.rowIcon} aria-hidden="true">
                  <Calendar size={18} />
                </span>
                <div className={styles.rowBody}>
                  {/* Tytułem wiersza jest przyczyna — nazwa sekcji stoi wyżej. */}
                  <p className={styles.rowTitle}>{t(`hrLeaves.sick.reasons.${item.reason}`)}</p>
                  <p className={styles.rowMeta}>
                    {t('portal.leaves.sickRange', {
                      from: formatDate(item.dateFrom, 'dot'),
                      to: formatDate(item.dateTo, 'dot'),
                      count: item.days,
                    })}
                  </p>
                  {item.ezlaNumber !== null ? (
                    <p className={styles.rowNote}>
                      {t('hrLeaves.sick.ezlaValue', { number: item.ezlaNumber })}
                    </p>
                  ) : null}
                </div>
              </div>
            ))}
          </Card>
        )}
      </section>

      <LeaveRequestModal open={formOpen && canRequest} onClose={() => setFormOpen(false)} />
    </>
  )
}
